import { t, Trans } from "@lingui/macro";
import { Modal } from "antd";
import React from "react";
import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { formatNumber } from "../../../../../components/FormatNumber";
import { ArrowRightOutlined } from "@ant-design/icons";
import _ from "underscore";
import { getListItemsTitle, renderBasicSecondaryListItem,
    renderTableItemsChanges, processInvalidListItems, renderSecondaryListItemWithItem,
} from "./helper";
import { StyledTag } from "./style";

// Show this modal if user chooses to restore autosaved data, but some stored data are no longer applicable.
// eg MANAGE/MUTLI features (UOM, tags), link items, list items etc.
// All inapplicable data will be shown in this modal, and the variable names are like so: allXChanges, eg allFormItemsChanges, allSubsChanges.
export function showAutosaveConfirmationModalPromise({
    // currencyCode,
    autosaveModalRef,

    // for link/linked items
    invalidLinkedItemArr,
    tooMuchAppliedArr,
    updatedLinkedItemAppliedArr,

    // for subscription
    nonBaseUomItems,
    removedBundles,
    subsChanges,
    productList,

    // for List API items
    listChanges,
}) {
    // ----- 1. For List API items -----
    const {
        invalidAccounts,
        invalidCPs,
        invalidContacts,
        invalidTags,
        invalidTerms,
        invalidNumberFormats,
        invalidCustomFields,
        // Below are for table items, and will have structure eg:
        // {
        //      accounts: ['Cash On Hand', 'Sales Income'],
        //      products: ['Apple'],
        // }
        invalidFormItemsObj,
        invalidTermItemsObj,
        invalidDepositItemsObj,
        invalidStockItemsObj,
        invalidBankItemsObj,
        invalidJournalItemsObj,
    } = listChanges;

    // Non-table list items changes
    const allNonTableListItemsChanges =
    [
        { arr: invalidAccounts, title: <Trans>Accounts:</Trans> },
        { arr: invalidContacts, title: <Trans>Contacts:</Trans> },
        { arr: invalidCPs, title: <Trans>Contact Persons:</Trans> },
        { arr: invalidTags, title: <Trans>Tags:</Trans> },
        { arr: invalidTerms, title: <Trans>Payment Terms:</Trans> },
    ]
        .reduce((acc, { arr, title }) => {
            acc.push(...processInvalidListItems(arr, title));
            return acc;
        }, []);

    // Table items list changes
    const allFormItemsChanges = [];
    const allTermItemsChanges = [];
    const allDepositItemsChanges = [];
    const allStockItemsChanges = [];
    const allJournalItemsChanges = [];
    const allBankItemsChanges = [];

    [
        { listArr: invalidFormItemsObj, accArr: allFormItemsChanges },
        { listArr: invalidTermItemsObj, accArr: allTermItemsChanges },
        { listArr: invalidDepositItemsObj, accArr: allDepositItemsChanges },
        { listArr: invalidBankItemsObj, accArr: allBankItemsChanges },
        { listArr: invalidJournalItemsObj, accArr: allJournalItemsChanges },
        { listArr: invalidStockItemsObj, accArr: allStockItemsChanges },
    ]
        .forEach(({ listArr }, idx, arr) => {
            Object.keys(listArr).forEach(key => {
                arr[idx].accArr.push(...processInvalidListItems(listArr[key], getListItemsTitle(key)));
            });
        });

    // ----- 2. For subscription -----
    const {
        hasTaxChange,
        hasLocationChange,
        hasTagChange,
        hasCurrencyCodeChange,
        hasFieldChange,
    } = subsChanges;

    const allSubsChanges = [
        {
            condition: hasTagChange,
            title: <Trans>All tags will be removed.</Trans>},
        {
            condition: hasCurrencyCodeChange,
            title: <Trans>Currency will be reverted to the company&apos;s currency.</Trans>,
            isBold: true,
        },
        {
            condition: hasLocationChange,
            title: <Trans>All product locations will be reverted to the default inventory location.</Trans>,
        },
        {
            condition: hasTaxChange,
            title: <Trans>All tax applied on products will be removed.</Trans>,
        },
        {
            condition: hasFieldChange,
            title: <Trans>All custom field values will be removed.</Trans>,
        },
        {
            // removed bundles
            condition: removedBundles?.length && removedBundles.length !== 0,
            title: <Trans>All product bundles will be removed.</Trans>,
        }]
        .filter(({ condition }) => !!condition)
        .map(({ title, isBold }) => {
            if (isBold)
                return (<strong>{title}</strong>);
            return title;
        });

    // Removed items with non-default UOM
    if (nonBaseUomItems?.length && nonBaseUomItems.length !== 0) {
        // const invalidUoms = Array.from(new Set(nonBaseUomItems.map(i => i.product_unit_label)));
        let invalidUoms = [];
        nonBaseUomItems.forEach(item => {
            const product = productList.find(p => p.id === item.product_id);
            invalidUoms.push(product?.units.find(u => u.id === item.product_unit_id)?.label);
        });

        invalidUoms = _.uniq(invalidUoms);
        allSubsChanges.push(
            <>
                <Trans>Lines with the following UOMs will be removed:</Trans>
                {renderBasicSecondaryListItem(invalidUoms)}
            </>,
        );
    }

    // ----- 3. For link/linked items -----
    const hasLinkedItemsChanges = updatedLinkedItemAppliedArr.length !== 0 ||
    invalidLinkedItemArr.length !== 0 || tooMuchAppliedArr.length !== 0;

    const allLinkedItemsChanges = hasLinkedItemsChanges ? (
        <ul className="ul__disc">
            { tooMuchAppliedArr.length !== 0 ? (
                <li key={Math.random()}>
                    <Trans><strong>Ignored due to excess apply amount:</strong></Trans>
                    {renderSecondaryListItemWithItem(tooMuchAppliedArr, (item => (
                        <StyledTag>
                            <Trans>
                                {`Balance: ${formatNumber(item.fetchedBalance, 2)}, 
                                Apply Amount: ${formatNumber(item.prevApply, 2)}`}
                            </Trans>
                        </StyledTag>
                    )),
                    )}
                </li>) : null }

            { updatedLinkedItemAppliedArr.length !== 0 ? (
                <li key={Math.random()}>
                    <Trans><strong>Updated apply amount:</strong></Trans>
                    {renderSecondaryListItemWithItem(updatedLinkedItemAppliedArr, (item => (
                        <>
                            <StyledTag>
                                <span className="span__prev-apply-amount">{formatNumber(item.prevApply, 2)}</span>
                                <ArrowRightOutlined />
                                <span><strong>{formatNumber(item.toApply, 2)}</strong></span>
                            </StyledTag>
                            {item.toApply === 0 ? <span> (will be removed)</span> : null}
                        </>
                    )),
                    )}
                </li>) : null }

            { invalidLinkedItemArr.length !== 0 ? (
                <li key={Math.random()}>
                    <Trans><strong>Removed:</strong></Trans>
                    {renderBasicSecondaryListItem(invalidLinkedItemArr)}
                </li>) : null }
        </ul>
    ) : [];

    // 4. ----- All render functions -----
    const renderAllListItemChanges = () => (
        <>
            { allNonTableListItemsChanges.length !== 0 ? (
                <div>
                    <Trans>
                        <strong>
                            The following items will be removed:
                        </strong>
                    </Trans>
                    <ul className="ul__disc">
                        {allNonTableListItemsChanges.map(
                            change => <li key={Math.random()}>{change}</li>)}
                    </ul>
                    <br />
                </div>
            ) : null }
            { invalidNumberFormats.length !== 0 ? (
                <div>
                    <Trans>
                        <strong>
                            Number format will be reverted to the default value:
                        </strong>
                    </Trans>
                    <ul className="ul__disc">
                        {invalidNumberFormats.map(item => (
                            <li key={Math.random()}>
                                <StyledTag>
                                    <span className="span__prev-apply-amount">{item.old}</span>
                                    <ArrowRightOutlined />
                                    <span><strong>{item.new}</strong></span>
                                </StyledTag>
                            </li>
                        ),
                        )}
                    </ul>
                    <br />
                </div>
            ) : null }
            { invalidCustomFields.length !== 0 ? (
                <div>
                    <Trans>
                        <strong>
                            The following custom fields will be removed:
                        </strong>
                    </Trans>
                    <ul className="ul__disc">
                        {invalidCustomFields.map(
                            obj => (
                                <li key={Math.random()}>
                                    { `${obj.field_name}: ${obj.field_value}`}
                                </li>
                            ))}
                    </ul>
                    <br />
                </div>
            ) : null}
            {[
                { arr: allFormItemsChanges, title: <Trans>Changes to Form Items:</Trans> },
                { arr: allTermItemsChanges, title: <Trans>Changes to Term Items:</Trans> },
                { arr: allDepositItemsChanges, title: <Trans>Changes to Deposit Items:</Trans> },
                { arr: allStockItemsChanges, title: <Trans>Changes to Stock Items:</Trans> },
                { arr: allBankItemsChanges, title: <Trans>Changes to Bank Items:</Trans> },
                { arr: allJournalItemsChanges, title: <Trans>Changes to Form Items:</Trans> },
            ]
                .map(({arr, title}) => (arr.length !== 0 ? renderTableItemsChanges(arr, title) : null))}
        </>
    );

    const renderAllSubsChanges = () => (
        allSubsChanges.length !== 0 ? (
            <div>
                <strong>
                    <Trans>Not-in-subscription Features:</Trans>
                </strong>
                <ul className="ul__disc">
                    {allSubsChanges.map(change => <li key={Math.random()}>{change}</li>)}
                </ul>
                <br />
            </div>
        ) : null
    );

    const renderAllLinkedItemChanges = () => (
        allLinkedItemsChanges.length !== 0 ? (
            <div>
                <strong>
                    <Trans>Changes to Linked items:</Trans>
                </strong>
                {allLinkedItemsChanges}
            </div>
        ) : null
    );

    // ----- 5. Return -----
    return new Promise(resolve => {
        // eslint-disable-next-line no-param-reassign
        autosaveModalRef.current = Modal.warning();

        // Set the resolve function, to be called when onPageChange in each txn's new.js
        // eslint-disable-next-line no-param-reassign
        autosaveModalRef.current.resolve = resolve;

        autosaveModalRef.current.update({
            title: t`Review Fallback Changes`,
            content: (
                <I18nProvider i18n={i18n} forceRenderOnLocaleChange={false}>
                    <div>
                        <div>
                            <Trans>
                                This autosaved transaction has updates that
                                require your attention. Please review fallback
                                changes that will be applied to the transaction.
                            </Trans>
                        </div>
                        <br />
                        <div>
                            {renderAllListItemChanges()}
                            {renderAllSubsChanges()}
                            {renderAllLinkedItemChanges()}
                        </div>
                    </div>
                </I18nProvider>
            ),
            onOk() {
                resolve();
            },
            okText: t`OK`,
            keyboard: false,
        });
    });
}
